/**
 * Maps raw ledger transaction types (seamless_bet, bet_payout, promo_bonus,
 * cashback_convert, …) onto player-facing presentation: a filter category, an
 * i18n label key, and an icon. Players never see machine type names or the
 * internal colon-delimited reference strings.
 */
import type { LucideIcon } from 'lucide-react';
import {
  Dices,
  Trophy,
  ArrowDownToLine,
  ArrowUpFromLine,
  Gift,
  Coins,
  Users,
  SlidersHorizontal,
  Undo2,
  Receipt,
  CalendarCheck,
} from 'lucide-react';
import type { WalletTransaction } from './api.js';

export type TxCategory =
  | 'bet'
  | 'deposit'
  | 'withdraw'
  | 'promo'
  | 'cashback'
  | 'commission'
  | 'adjust'
  | 'other';

export interface TxPresentation {
  category: TxCategory;
  /** i18n key under wallet `txTypes.*` — falls back to the category label. */
  labelKey: string;
  icon: LucideIcon;
  /** Net effect on balance: + credit, − debit, 0 neutral. */
  direction: 1 | -1 | 0;
}

const ICON_BY_TYPE: Record<string, LucideIcon> = {
  seamless_bet: Dices,
  bet_place: Dices,
  seamless_win: Trophy,
  bet_payout: Trophy,
  bet_settle_win: Trophy,
  seamless_jackpot: Trophy,
  seamless_freegame: Gift,
  seamless_promo: Gift,
  seamless_bonus: Gift,
  seamless_refund: Undo2,
  bet_cancel: Undo2,
  payout_cancel: Undo2,
  deposit: ArrowDownToLine,
  withdraw: ArrowUpFromLine,
  withdraw_reject_refund: Undo2,
  promo_bonus: Gift,
  promo_credit: Gift,
  promo_convert: Coins,
  bonus_convert_debit: Coins,
  bonus_convert_credit: Coins,
  bonus_forfeit: Undo2,
  cashback: Coins,
  cashback_convert: Coins,
  commission_transfer: Users,
  referral_commission: Users,
  reward_credit: Gift,
  daily_checkin: CalendarCheck,
  admin_add: SlidersHorizontal,
  admin_sub: SlidersHorizontal,
  adjust: SlidersHorizontal,
};

const ICON_BY_CATEGORY: Record<TxCategory, LucideIcon> = {
  bet: Dices,
  deposit: ArrowDownToLine,
  withdraw: ArrowUpFromLine,
  promo: Gift,
  cashback: Coins,
  commission: Users,
  adjust: SlidersHorizontal,
  other: Receipt,
};

/** Same grouping the backend uses for category filtering — keep in sync. */
export function categorize(type: string): TxCategory {
  if (type.startsWith('seamless') || type.startsWith('bet_') || type.startsWith('payout_')) return 'bet';
  if (type.startsWith('deposit')) return 'deposit';
  if (type.startsWith('withdraw')) return 'withdraw';
  if (
    type.startsWith('promo') ||
    type.startsWith('bonus') ||
    type.startsWith('reward_credit') ||
    type.startsWith('daily_checkin')
  ) {
    return 'promo';
  }
  if (type.startsWith('cashback')) return 'cashback';
  if (type.startsWith('commission') || type.startsWith('referral_commission')) return 'commission';
  if (type.startsWith('admin') || type.startsWith('adjust')) return 'adjust';
  return 'other';
}

// Every known type has an explicit label key; anything unrecognised falls back
// to its category label so the UI never shows a raw machine string.
export const KNOWN_LABEL_TYPES = new Set(Object.keys(ICON_BY_TYPE));

/**
 * Ledger types too coarse for the row, refined by `ref_kind`.
 *
 * The loss cashback and the turnover rebate are two promotions paying the same
 * ledger type — svc-promo's saga reply consumer finalises a rebate claim on
 * `type: 'cashback'`, so neither kind can have a type of its own — and the row
 * named neither of them (#409). svc-wallet stamps the kind on `ref_kind`.
 *
 * The bare `ref_kind: 'cashback'` is deliberately absent from this map: every
 * rebate credited before the kinds were split carries it whichever promotion
 * paid, so it falls through to the generic `txTypes.cashback` — the only honest
 * label for a row that never recorded which rebate it was.
 */
export const REF_KIND_LABEL_KEYS: Record<string, string> = {
  turnover_rebate: 'txRefKinds.turnover_rebate',
  loss_cashback: 'txRefKinds.loss_cashback',
};

export function presentTransaction(tx: WalletTransaction): TxPresentation {
  const category = categorize(tx.type);
  const byRefKind = tx.refKind ? REF_KIND_LABEL_KEYS[tx.refKind] : undefined;
  const labelKey =
    byRefKind ??
    (KNOWN_LABEL_TYPES.has(tx.type) ? `txTypes.${tx.type}` : `txCategoryLabel.${category}`);
  const icon = ICON_BY_TYPE[tx.type] ?? ICON_BY_CATEGORY[category];
  const direction: 1 | -1 | 0 = tx.amount > 0 ? 1 : tx.amount < 0 ? -1 : 0;
  return { category, labelKey, icon, direction };
}
